// Importaciones de redux
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import urls from '@src/@core/auth/jwt/urls'
import axios from 'axios'
import { sucessToast, errorToast } from "../../../components/messageToast/messageToast"

const axiosAuth = () => axios.create({
        baseURL: urls.url,
        headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
            'Content-Type': 'application/json'
        }
    })


export const getAllSellers = createAsyncThunk('appSellers/getAllSellers', async (params = {}) => {
    const instance = axiosAuth()
    const response = await instance.get('/customers/sellers/', {
        params: {
            page_size: params.page_size ?? 20,
            p: params.page ?? 1,
            search: params.search ?? '',
            ordering: params.ordering ?? '-created_date'
        }
    })
    return response.data
})

export const getSeller = createAsyncThunk('appSellers/getSeller', async (id) => {
    const instance = axiosAuth()
    const response = await instance.get(`/customers/sellers/${id}/`)
    return response.data
})

export const createSeller = createAsyncThunk('appSellers/createSeller', async (payload, { dispatch, rejectWithValue }) => {
    const instance = axiosAuth()
    try {
        const res = await instance.post('/customers/sellers/', payload)
        await dispatch(getAllSellers({ page: 1 }))
        sucessToast('Vendedor creado correctamente')
        return res.data
    } catch (err) {
        const data = err?.response?.data
        let msg = 'No se pudo crear el vendedor'
        if (data) {
            const firstKey = Object.keys(data)[0]
            if (Array.isArray(data[firstKey])) {
                msg = data[firstKey][0]
            } else if (typeof data.detail === 'string') {
                msg = data.detail
            }
        }
        errorToast(msg)
        return rejectWithValue(msg)
    }
})

export const updateSeller = createAsyncThunk('appSellers/updateSeller', async ({ id, payload }, { dispatch, rejectWithValue }) => {
    const instance = axiosAuth()
    try {
        const res = await instance.put(`/customers/sellers/${id}/`, payload)
        await dispatch(getSeller(id))
        sucessToast('Vendedor actualizado correctamente')
        return res.data
    } catch (err) {
        const data = err?.response?.data
        let msg = 'No se pudo actualizar el vendedor'
        if (data) {
            const firstKey = Object.keys(data)[0]
            if (Array.isArray(data[firstKey])) {
                msg = data[firstKey][0]
            } else if (typeof data.detail === 'string') {
                msg = data.detail
            }
        }
        errorToast(msg)
        return rejectWithValue(msg)
    }
})

export const deleteSeller = createAsyncThunk('appSellers/deleteSeller', async (id, { dispatch, rejectWithValue }) => {
    const instance = axiosAuth()

    try {
        await instance.delete(`/customers/sellers/${id}/`)
        await dispatch(getAllSellers({ page: 1 }))
        sucessToast('Vendedor eliminado')
        return true
    } catch (err) {
        const data = err?.response?.data
        let msg = 'No fue posible eliminar el vendedor'
        if (data) {
            const firstKey = Object.keys(data)[0]
            if (Array.isArray(data[firstKey])) {
                msg = data[firstKey][0]
            } else if (typeof data.detail === 'string') {
                msg = data.detail
            }
        }
        errorToast(msg)
        return rejectWithValue(msg)
    }
})

// Contactos del seller
export const createSellerContact = createAsyncThunk(
    'appSellers/createSellerContact',
    async ({ sellerId, payload }, { dispatch, rejectWithValue }) => {
        const instance = axiosAuth()
        try {
            const res = await instance.post('/customers/seller-contacts/', {
                seller: sellerId,
                ...payload
            })
            // refresca seller para traer contacts actualizados
            await dispatch(getSeller(sellerId))
            sucessToast('Contacto creado')
            return res.data
        } catch (err) {
            const data = err?.response?.data
            let msg = data?.detail || 'No se pudo crear el contacto'
            if (data) {
                const firstKey = Object.keys(data)[0]
                const value = data[firstKey]

                if (Array.isArray(value)) msg = value[0]
                else if (typeof value === 'string') msg = value
                else if (typeof data.detail === 'string') msg = data.detail
            }
            errorToast(msg)
            return rejectWithValue(msg)
        }
    }
)

export const updateSellerContact = createAsyncThunk(
    'appSellers/updateSellerContact',
    async ({ id, sellerId, payload }, { dispatch, rejectWithValue }) => {
        const instance = axiosAuth()
        try {
            const res = await instance.patch(`/customers/seller-contacts/${id}/`, payload)
            await dispatch(getSeller(sellerId))
            sucessToast('Contacto actualizado')
            return res.data
        } catch (err) {
            const data = err?.response?.data
            const msg = data?.detail || 'No se pudo actualizar el contacto'
            errorToast(msg)
            return rejectWithValue(msg)
        }
    }
)

export const deleteSellerContact = createAsyncThunk(
    'appSellers/deleteSellerContact',
    async ({ id, sellerId }, { dispatch, rejectWithValue }) => {
        const instance = axiosAuth()
        try {
            await instance.delete(`/customers/seller-contacts/${id}/`)
            await dispatch(getSeller(sellerId))
            sucessToast('Contacto eliminado')
            return true
        } catch (err) {
            const data = err?.response?.data
            const msg = data?.detail || 'No se pudo eliminar el contacto'
            errorToast(msg)
            return rejectWithValue(msg)
        }
    }
)


export const setIsFetchingSellers = createAsyncThunk('appSellers/setIsFetchingSellers', () => 'fetching')
export const cleanSellers = createAsyncThunk('appSellers/cleanSellers', () => 'clean')

export const appSellersSlice = createSlice({
    name: 'appSellers',
    initialState: {
        allData: [],
        total: 0,
        loading: false,
        isFetching: false,
        seller: null,
        error: null
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            // list
            .addCase(getAllSellers.fulfilled, (state, action) => {
                // DRF paginado: { results, count }
                const data = action.payload
                state.allData = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : [])
                state.total = typeof data?.count === 'number' ? data.count : state.allData.length
                state.isFetching = false
            })
            .addCase(getAllSellers.rejected, (state, action) => {
                state.isFetching = false
                state.error = action.error?.message ?? 'Error cargando vendedores'
            })

            // retrieve
            // .addCase(getSeller.pending, (state) => {
            //     state.isFetching = true
            //     state.error = null
            // })
            .addCase(getSeller.fulfilled, (state, action) => {
                state.seller = action.payload
                state.isFetching = false
            })
            .addCase(getSeller.rejected, (state, action) => {
                state.isFetching = false
                state.error = action.error?.message ?? 'Error cargando vendedor'
            })

            // .addCase(createSeller.pending, (state) => {
            //     state.loading = true
            //     state.error = null
            // })
            .addCase(createSeller.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(createSeller.rejected, (state, action) => {
                state.loading = false
                state.error = action.error?.message ?? 'Error creando vendedor'
            })

            // .addCase(updateSeller.pending, (state) => {
            //     state.loading = true
            //     state.error = null
            // })
            .addCase(updateSeller.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(updateSeller.rejected, (state, action) => {
                state.loading = false
                state.error = action.error?.message ?? 'Error actualizando vendedor'
            })

            // .addCase(deleteSeller.pending, (state) => {
            //     state.loading = true
            //     state.error = null
            // })
            .addCase(deleteSeller.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(deleteSeller.rejected, (state, action) => {
                state.loading = false
                state.error = action.error?.message ?? 'Error eliminando vendedor'
            })

            .addCase(createSellerContact.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(createSellerContact.rejected, (state, action) => {
                state.loading = false
                state.error = action.error?.message ?? 'Error actualizando vendedor'
            })

            .addCase(updateSellerContact.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(updateSellerContact.rejected, (state, action) => {
                state.loading = false
                state.error = action.error?.message ?? 'Error actualizando contacto de  vendedor'
            })

            .addCase(deleteSellerContact.fulfilled, (state) => {
                state.loading = false
            })
            .addCase(deleteSellerContact.rejected, (state, action) => {
                state.loading = false
                state.error = action.error?.message ?? 'Error eliminando contacto de vendedor'
            })

            .addCase(setIsFetchingSellers.fulfilled, (state) => {
                state.isFetching = true
            })
            .addCase(cleanSellers.fulfilled, (state) => {
                state.allData = []
                state.total = 0
                state.seller = null
                state.error = null
            })
    }
})

export default appSellersSlice.reducer